import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

const initialState = {
  tasks: [],
  board: {
    columns: [],
    totalTasks: 0,
    sprint: null,
    isBacklog: false,
  },
  currentTask: null,
  pagination: {
    total: 0,
    page: 1,
    pageSize: 20,
    totalPages: 1,
  },
  loading: false,
  boardLoading: false,
  error: null,
  // Board filters
  boardFilters: {
    sprintId: null,
    backlogOnly: false,
    assigneeId: null,
  },
};

// Async thunks
export const fetchTasks = createAsyncThunk(
  'tasks/fetchTasks',
  async ({ projectId, page = 1, pageSize = 500, filters = {} }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', pageSize);

      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await api.get(`/projects/${projectId}/tasks?${params.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to fetch tasks'
      );
    }
  }
);

export const fetchBoard = createAsyncThunk(
  'tasks/fetchBoard',
  async ({ projectId, sprintId = null, backlogOnly = false, assigneeId = null, statuses = null, taskType = null, priority = null }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (sprintId) params.append('sprint_id', sprintId);
      if (backlogOnly) params.append('backlog_only', 'true');
      if (assigneeId) params.append('assignee_id', assigneeId);
      if (statuses && statuses.length > 0) {
        statuses.forEach(status => params.append('statuses', status));
      }
      if (taskType) params.append('task_type', taskType);
      if (priority) params.append('priority', priority);

      const url = `/projects/${projectId}/board${params.toString() ? '?' + params.toString() : ''}`;
      const response = await api.get(url);
      return { ...response.data, filters: { sprintId, backlogOnly, assigneeId, statuses, taskType, priority } };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to fetch board'
      );
    }
  }
);

export const fetchTaskById = createAsyncThunk(
  'tasks/fetchTaskById',
  async (taskId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/tasks/${taskId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to fetch task'
      );
    }
  }
);

export const createTask = createAsyncThunk(
  'tasks/createTask',
  async ({ projectId, taskData }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/projects/${projectId}/tasks`, taskData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to create task'
      );
    }
  }
);

export const updateTask = createAsyncThunk(
  'tasks/updateTask',
  async ({ taskId, taskData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/tasks/${taskId}`, taskData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to update task'
      );
    }
  }
);

export const updateTaskStatus = createAsyncThunk(
  'tasks/updateTaskStatus',
  async ({ taskId, status, position }, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/tasks/${taskId}/status`, { status, position });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to update status'
      );
    }
  }
);

export const deleteTask = createAsyncThunk(
  'tasks/deleteTask',
  async (taskId, { rejectWithValue }) => {
    try {
      await api.delete(`/tasks/${taskId}`);
      return taskId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail ||
        (error.response?.status === 403 ? 'You do not have permission to delete this task' : 'Failed to delete task')
      );
    }
  }
);

export const updateTaskPositions = createAsyncThunk(
  'tasks/updateTaskPositions',
  async ({ projectId, updates }, { rejectWithValue }) => {
    try {
      await api.put(`/projects/${projectId}/tasks/positions`, { updates });
      return updates;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to update positions'
      );
    }
  }
);

// Comments
export const addComment = createAsyncThunk(
  'tasks/addComment',
  async ({ taskId, content }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/tasks/${taskId}/comments`, { content });
      return { taskId, comment: response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to add comment'
      );
    }
  }
);

export const updateComment = createAsyncThunk(
  'tasks/updateComment',
  async ({ commentId, content }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/comments/${commentId}`, { content });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to update comment'
      );
    }
  }
);

export const deleteComment = createAsyncThunk(
  'tasks/deleteComment',
  async (commentId, { rejectWithValue }) => {
    try {
      await api.delete(`/comments/${commentId}`);
      return commentId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to delete comment'
      );
    }
  }
);

export const addReply = createAsyncThunk(
  'tasks/addReply',
  async ({ taskId, parentCommentId, content }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/tasks/${taskId}/comments/reply`, {
        parent_comment_id: parentCommentId,
        content
      });
      return { taskId, parentCommentId, reply: response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to add reply'
      );
    }
  }
);

export const fetchThreadedComments = createAsyncThunk(
  'tasks/fetchThreadedComments',
  async (taskId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/tasks/${taskId}/comments/threaded`);
      return { taskId, comments: response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to fetch comments'
      );
    }
  }
);

const tasksSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    clearCurrentTask: (state) => {
      state.currentTask = null;
    },
    clearTasks: (state) => {
      state.tasks = [];
    },
    clearError: (state) => {
      state.error = null;
    },
    optimisticBoardUpdate: (state, action) => {
      state.board.columns = action.payload;
    },
    setBoardFilters: (state, action) => {
      state.boardFilters = { ...state.boardFilters, ...action.payload };
    },
    clearBoardFilters: (state) => {
      state.boardFilters = {
        sprintId: null,
        backlogOnly: false,
        assigneeId: null,
      };
    },
    reorderTasks: (state, action) => {
      action.payload.forEach(({ id, position }) => {
        const task = state.tasks.find(t => t.id === id);
        if (task) task.position = position;
      });
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch tasks
      .addCase(fetchTasks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks = action.payload.tasks;
        state.pagination = {
          total: action.payload.total,
          page: action.payload.page,
          pageSize: action.payload.page_size,
          totalPages: action.payload.total_pages,
        };
      })
      .addCase(fetchTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch board
      .addCase(fetchBoard.pending, (state) => {
        state.boardLoading = true;
      })
      .addCase(fetchBoard.fulfilled, (state, action) => {
        state.boardLoading = false;
        state.board = {
          columns: action.payload.columns,
          totalTasks: action.payload.total_tasks,
          sprint: action.payload.sprint || null,
          isBacklog: action.payload.is_backlog || false,
        };
        if (action.payload.filters) {
          state.boardFilters = action.payload.filters;
        }
      })
      .addCase(fetchBoard.rejected, (state, action) => {
        state.boardLoading = false;
        state.error = action.payload;
      })
      // Fetch task by ID
      .addCase(fetchTaskById.pending, (state) => {
        state.loading = true;
        // Clear previous task to prevent stale data (comments, activities) from showing
        state.currentTask = null;
      })
      .addCase(fetchTaskById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTask = action.payload;
      })
      .addCase(fetchTaskById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create task
      .addCase(createTask.fulfilled, (state, action) => {
        state.tasks.unshift(action.payload);
        // Update board if exists
        if (state.board.columns.length > 0) {
          const backlogCol = state.board.columns.find(c => c.status === 'backlog');
          if (backlogCol) {
            backlogCol.tasks.unshift(action.payload);
            state.board.totalTasks += 1;
          }
        }
      })
      // Update task
      .addCase(updateTask.fulfilled, (state, action) => {
        const index = state.tasks.findIndex(t => t.id === action.payload.id);
        const oldSprintId = index !== -1 ? state.tasks[index].sprint_id : undefined;
        if (index !== -1) {
          state.tasks[index] = action.payload;
        }
        if (state.currentTask?.id === action.payload.id) {
          state.currentTask = { ...state.currentTask, ...action.payload };
        }
        // Update in board
        state.board.columns.forEach(col => {
          const taskIndex = col.tasks.findIndex(t => t.id === action.payload.id);
          if (taskIndex !== -1) {
            col.tasks[taskIndex] = action.payload;
          }
        });
        // Cascade sprint change (both directions) to subtasks in store
        if (oldSprintId !== action.payload.sprint_id) {
          state.tasks.forEach(t => {
            if (t.parent_id === action.payload.id) {
              t.sprint_id = action.payload.sprint_id;
            }
          });
        }
      })
      // Update task status
      .addCase(updateTaskStatus.fulfilled, (state, action) => {
        // Update in tasks list
        const index = state.tasks.findIndex(t => t.id === action.payload.id);
        const oldSprintId = index !== -1 ? state.tasks[index].sprint_id : undefined;
        if (index !== -1) {
          state.tasks[index] = action.payload;
        }
        // If sprint was cleared on the parent (backlog status), cascade to subtasks in store
        if (oldSprintId != null && action.payload.sprint_id == null) {
          state.tasks.forEach(t => {
            if (t.parent_id === action.payload.id) {
              t.sprint_id = null;
            }
          });
        }
      })
      // Delete task
      .addCase(deleteTask.fulfilled, (state, action) => {
        state.tasks = state.tasks.filter(t => t.id !== action.payload);
        // Remove from board
        state.board.columns.forEach(col => {
          col.tasks = col.tasks.filter(t => t.id !== action.payload);
        });
        state.board.totalTasks -= 1;
      })
      // Add comment
      .addCase(addComment.fulfilled, (state, action) => {
        if (state.currentTask && state.currentTask.id === action.payload.taskId) {
          state.currentTask.comments.push(action.payload.comment);
        }
      })
      // Update comment
      .addCase(updateComment.fulfilled, (state, action) => {
        if (state.currentTask) {
          const index = state.currentTask.comments.findIndex(
            c => c.id === action.payload.id
          );
          if (index !== -1) {
            state.currentTask.comments[index] = action.payload;
          }
        }
      })
      // Delete comment
      .addCase(deleteComment.fulfilled, (state, action) => {
        if (state.currentTask) {
          state.currentTask.comments = state.currentTask.comments.filter(
            c => c.id !== action.payload
          );
        }
      })
      // Add reply
      .addCase(addReply.fulfilled, (state, action) => {
        // Refresh the task to get updated threaded comments
        // The reply will be fetched with threaded comments
      })
      // Fetch threaded comments
      .addCase(fetchThreadedComments.fulfilled, (state, action) => {
        if (state.currentTask && state.currentTask.id === action.payload.taskId) {
          state.currentTask.threadedComments = action.payload.comments;
        }
      });
  },
});

export const { clearCurrentTask, clearTasks, clearError, optimisticBoardUpdate, setBoardFilters, clearBoardFilters, reorderTasks } = tasksSlice.actions;
export default tasksSlice.reducer;
